<template>
  <div class="StockStatusFutureAvailability">
    <LoadingIcon v-if="futureDeliveriesLoading" />
    <div v-else-if="futureDeliveriesDisplay.display">
      <div class="flex flex-wrap items-center gap-12">
        <div
          v-for="(fdRow, index) in futureDeliveriesDisplay.rows"
          :key="index"
          class="flex items-center gap-6"
        >
          <div
            class="w-8 h-8 rounded-full border relative top-1 bg-bg-successStrong border-border-successStrong"
            :class="{
              'bg-bg-successStrong border-border-successStrong': fdRow.color === 'green',
              'bg-bg-warning border-border-warningStrong': fdRow.color === 'yellow',
              'bg-bg-warningStrong border-border-warningStrong': fdRow.color === 'red',
            }"
          />
          <div>{{ fdRow.text }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import NP from 'number-precision';
import type { ProductVariantModel } from '#models/productVariant';
import type { ProductModel } from '#models/product';
import usePackageAreaConverter from '~/composables/usePackageAreaConverter';
import {
  type FutureDeliveriesDisplay,
  checkForFutureDelivery
} from '~/composables/useStockStatus';
import { useCartStore } from '~/store/cart';
import type { CartApiItem } from '#constants/types/norce';
import { NorceCartItemStatusId } from '#constants/norceCodes';
import useWarehouse from '~/composables/useWarehouse';

const cartStore = useCartStore();
const { getVariantStockValue } = useWarehouse();

NP.enableBoundaryChecking(false);

/*
interface Props {
  activeVariant: ProductVariantModel,
  totalQuantity?: number
}

const props = withDefaults(defineProps<Props>(), {
  totalQuantity: 1,
});
*/

const props = defineProps<{
  activeVariant?: ProductVariantModel,
  cartItem?: CartApiItem
  totalQuantity: number,
  productItem?: ProductModel
}>();

const emit = defineEmits<{
  (e: 'setBuyButtonDisabled', payload: boolean): void;
  (e: 'loading', payload: boolean): void;
}>();

const stockvalue = computed(() => {
  if (props.activeVariant) { return getVariantStockValue(props.activeVariant); }
  if (props.cartItem?.onHandValue) { return props.cartItem.onHandValue; }
  return 0;
});

const partNo = computed(() => {
  if (props.activeVariant) { return props.activeVariant.partNo; }
  if (props.cartItem) { return props.cartItem.partNo; }
  return '';
});

const isExpiring = computed(() => {
  if (props.activeVariant) { return props.activeVariant.isExpiring; }
  if (props.cartItem) { return props.cartItem.statusId === NorceCartItemStatusId.Expiring; }
  return false;
});

const source = computed<'pdp' | 'cart'>(() => props.cartItem ? 'cart' : 'pdp');

const {
  unitDisplay,
  convertPackageToArea,
  shouldUseArea,
} = usePackageAreaConverter(toRef(()=>props.activeVariant ? props.activeVariant : props.cartItem));

const futureDeliveriesLoading = ref(true);

const futureDeliveriesDisplay = ref<FutureDeliveriesDisplay>({ display: false, lockBuyButton: false });

const isNewProduct = computed(() => props.productItem?.hasNewFlag || false);

onMounted(()=> {
  setTimeout(setFutureDeliveriesDisplay, 20);
});

const setFutureDeliveriesDisplay = async() => {
  const onHandArea = convertPackageToArea(stockvalue.value) || 0;
  const requestedArea = convertPackageToArea(props.totalQuantity) || 0;
  const alreadyInCart = cartStore.getQuantityFromPartNo(partNo.value);
  const inCartArea = convertPackageToArea(alreadyInCart) || 0;

  // if we are already in cart, we only get status for whats in the cart already
  const totalAreaNeeded = source.value === 'cart' ? inCartArea : NP.plus(requestedArea, inCartArea);

  futureDeliveriesLoading.value = true;
  emit('loading', true);
  futureDeliveriesDisplay.value = await checkForFutureDelivery(
    partNo.value,
    totalAreaNeeded,
    onHandArea,
    shouldUseArea.value,
    unitDisplay,
    isExpiring.value,
    isNewProduct.value
  );
  futureDeliveriesLoading.value = false;
  emit('loading', false);
  emit('setBuyButtonDisabled', futureDeliveriesDisplay.value.lockBuyButton);
};

watch(
  () => props.activeVariant,
  () => { setFutureDeliveriesDisplay(); }
);

watch(
  () => props.totalQuantity,
  () => { setFutureDeliveriesDisplay(); }
);

watch(
  () => cartStore.getQuantityFromPartNo(partNo.value),
  () => { setFutureDeliveriesDisplay(); }
);

</script>
